/**
 * 配置工具 - 从环境变量中读取配置
 */

/**
 * 环境类型定义
 */
export type Environment = 'development' | 'testing' | 'production';

/**
 * 获取当前环境
 * @returns Environment 当前环境类型
 */
export const getCurrentEnvironment = (): Environment => {
  const env = process.env.NODE_ENV || 'development';
  
  if (env === 'production') {
    return 'production';
  }
  
  // 可以通过特定环境变量来切换到测试环境
  if (process.env.REACT_APP_ENV === 'testing') {
    return 'testing';
  }
  
  return 'development';
};

/**
 * 获取API基础URL
 * 生产环境未配置 REACT_APP_API_BASE_URL 时使用当前站点同源（与边缘 Nginx /api 反代一致）
 * @returns string API基础URL
 */
export const getApiBaseUrl = (): string => {
  const fromEnv = process.env.REACT_APP_API_BASE_URL;
  if (fromEnv !== undefined && fromEnv !== null && String(fromEnv).trim() !== '') {
    return String(fromEnv).replace(/\/$/, '');
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return 'http://localhost:3000';
};

/**
 * 获取应用名称
 * @returns string 应用名称
 */
export const getAppName = (): string => {
  const env = getCurrentEnvironment();
  const baseName = process.env.REACT_APP_SITE_NAME || '山间码坊官网';
  
  switch (env) {
    case 'development':
      return process.env.REACT_APP_APP_NAME_DEVELOPMENT || `${baseName} - 开发环境`;
    case 'testing':
      return process.env.REACT_APP_APP_NAME_TESTING || `${baseName} - 测试环境`;
    case 'production':
    default:
      return baseName;
  }
};

/**
 * 是否为开发环境
 * @returns boolean 是否为开发环境
 */
export const isDevelopment = (): boolean => {
  return getCurrentEnvironment() === 'development';
};

/**
 * 是否为生产环境
 * @returns boolean 是否为生产环境
 */
export const isProduction = (): boolean => {
  return getCurrentEnvironment() === 'production';
};

/**
 * 是否为测试环境
 * @returns boolean 是否为测试环境
 */
export const isTesting = (): boolean => {
  return getCurrentEnvironment() === 'testing';
};

/**
 * 是否启用调试模式
 * @returns boolean 是否启用调试模式
 */
export const isDebugMode = (): boolean => {
  const debugFlag = process.env.REACT_APP_DEBUG_MODE;
  if (debugFlag !== undefined) {
    return debugFlag === 'true';
  }
  
  // 默认在开发和测试环境启用调试模式
  return isDevelopment() || isTesting();
};

/**
 * 获取日志级别
 * @returns 'error' | 'warn' | 'info' | 'debug' 日志级别
 */
export const getLogLevel = (): 'error' | 'warn' | 'info' | 'debug' => {
  const envLevel = process.env.REACT_APP_LOG_LEVEL;
  if (envLevel && ['error', 'warn', 'info', 'debug'].includes(envLevel)) {
    return envLevel as 'error' | 'warn' | 'info' | 'debug';
  }
  
  // 根据环境设置默认日志级别
  if (isProduction()) {
    return 'error';
  } else if (isTesting()) {
    return 'info';
  } else {
    return 'debug';
  }
};

/**
 * 获取API超时时间
 * @returns number 超时时间（毫秒）
 */
export const getApiTimeout = (): number => {
  const timeout = process.env.REACT_APP_API_TIMEOUT;
  if (timeout) {
    const parsedTimeout = parseInt(timeout, 10);
    if (!isNaN(parsedTimeout)) {
      return parsedTimeout;
    }
  }
  
  return 10000; // 默认10秒
};

/**
 * 获取完整的配置对象
 * 保留这个接口以保持向后兼容性
 * @returns 配置对象
 */
export const getConfig = () => {
  const env = getCurrentEnvironment();
  
  return {
    api_base_url: getApiBaseUrl(),
    app_name: getAppName(),
    environment: env,
    is_development: isDevelopment(),
    is_production: isProduction(),
    is_testing: isTesting(),
    debug_mode: isDebugMode(),
    log_level: getLogLevel()
  };
};